import { Grid } from '@mui/material';
import { useFormikContext } from 'formik';
import { Dispatch, memo, SetStateAction, useCallback } from 'react';

import { Checkbox } from '../../../../../form/Checkbox/Checkbox';
import { ResourcesPageFormValues } from '../../../../../pages/ResourcesPage';
import { StyledCheckbox, StyledMuiTextField } from './ResourceFieldsLineProperties.style';

export const ResourceFieldsLineCommonProperties = memo(function ResourceFieldsLineCommonProperties({
    index,
    arrayReplace,
    selectedResources,
    setSelectedResources,
}: {
    index: number;
    arrayReplace: (index: number, value: any) => void;
    selectedResources: number[];
    setSelectedResources: Dispatch<SetStateAction<number[]>>;
}) {
    const { errors, values, initialValues } = useFormikContext<ResourcesPageFormValues>();

    const handleSelection = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.checked) {
                setSelectedResources(selectedResources.concat(index));
            } else {
                setSelectedResources(selectedResources.filter(i => i !== index));
            }
        },
        [index, selectedResources, setSelectedResources],
    );

    const handleChange = useCallback(
        (prop: string, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            arrayReplace(index, {
                ...values.resources[index],
                [prop]: event.target.value,
            });
        },
        [arrayReplace, index, values.resources],
    );

    const handleNumberChange = useCallback(
        (prop: string, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            arrayReplace(index, {
                ...values.resources[index],
                [prop]: Number(event.target.value),
            });
        },
        [arrayReplace, index, values.resources],
    );

    const handleSelectChange = useCallback(() => {
        arrayReplace(index, {
            ...values.resources[index],
            active: !values.resources[index].active,
        });
    }, [arrayReplace, index, values.resources]);

    return (
        <>
            <Grid width={'40px'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <Checkbox label={''} checked={selectedResources.includes(index)} onChange={handleSelection} />
            </Grid>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledMuiTextField
                    variant="filled"
                    value={values.resources[index].name}
                    error={!!errors[`resources[${index}]['name']`]}
                    helperText={errors[`resources[${index}]['name']`]}
                    disabled={!!values.resources[index].id}
                    onChange={e => handleChange('name', e)}
                />
            </Grid>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledMuiTextField
                    variant="filled"
                    modified={
                        values.resources[index].id
                            ? values.resources[index].shortName !== initialValues.resources[index]?.shortName
                            : undefined
                    }
                    value={values.resources[index].shortName}
                    onChange={e => handleChange('shortName', e)}
                />
            </Grid>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledCheckbox
                    label={''}
                    checked={values.resources[index].active}
                    onChange={handleSelectChange}
                    modified={
                        values.resources[index].id
                            ? values.resources[index].active !== initialValues.resources[index]?.active
                            : undefined
                    }
                />
            </Grid>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledMuiTextField
                    type={'number'}
                    variant="filled"
                    modified={
                        values.resources[index].id
                            ? values.resources[index].capacity !== initialValues.resources[index]?.capacity
                            : undefined
                    }
                    error={!!errors[`resources[${index}]['capacity']`]}
                    helperText={errors[`resources[${index}]['capacity']`]}
                    value={values.resources[index].capacity}
                    InputProps={{
                        inputProps: { min: 1 },
                    }}
                    onChange={e => handleNumberChange('capacity', e)}
                />
            </Grid>
            <Grid item xs display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <StyledMuiTextField
                    type={'number'}
                    variant="filled"
                    modified={
                        values.resources[index].id
                            ? values.resources[index].minTimeBetweenUse !==
                              initialValues.resources[index]?.minTimeBetweenUse
                            : undefined
                    }
                    InputProps={{
                        inputProps: { min: 0 },
                    }}
                    error={!!errors[`resources[${index}]['minTimeBetweenUse']`]}
                    helperText={errors[`resources[${index}]['minTimeBetweenUse']`]}
                    value={values.resources[index].minTimeBetweenUse}
                    onChange={e => handleNumberChange('minTimeBetweenUse', e)}
                />
            </Grid>
        </>
    );
});
